/**
 * <christmas-vote> — the Christmas design vote page.
 *
 * Shows a countdown to the deadline and lets a visitor vote once: the vote
 * button opens a sheet where the email locks the vote in (so a stray tap while
 * scrolling does not spend it). The vote is recorded first, then Shopify's
 * customer form takes the email and reloads the page with the $5 code; the
 * live results show from then on and stay fresh while the page is visible.
 * The vote is stored with a hash of the email, so in another browser or a
 * private window "Already voted?" (or voting again) finds it in the sheet.
 * The data layer lives in christmas-vote-store.js.
 */
class ChristmasVote extends HTMLElement {
  static POLL_INTERVAL_MS = 10000;
  static TOP_PLACES = 8;
  static SECOND_MS = 1000;

  connectedCallback() {
    this.strings = JSON.parse(this.querySelector('[data-vote-strings]').textContent);
    this.closesAt = new Date(this.dataset.closesAt).getTime();
    this.store = new window.ChristmasVoteStore({
      endpoint: this.dataset.endpoint,
      customerId: this.dataset.customerId,
    });
    this.cards = Array.from(this.querySelectorAll('[data-design-id]'));
    this.dialog = this.querySelector('[data-vote-dialog]');
    this.form = this.dialog.querySelector('form');
    this.vote = this.store.readVote();
    // Shopify can return through a challenge or a browser with storage blocked.
    // Recover only a vote confirmed by the backend, never a design from the URL.
    this.returningVoter = new URLSearchParams(window.location.search).get('vote_voter');
    this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en');

    this.bindEvents();
    this.tickCountdown();
    this.countdownTimer = setInterval(() => this.tickCountdown(), ChristmasVote.SECOND_MS);

    if (this.vote) {
      this.markVoted(this.vote.designId);
      const results = this.store.readResults();
      if (results) this.renderResults(results);
    }
    // After the deadline tickCountdown() has already loaded the results.
    if ((this.vote || this.returningVoter || this.dataset.customerId) && !this.isClosed()) this.refreshResults();
    const initialPanel = this.dialog.querySelector('[data-vote-initial-panel]');
    if (initialPanel) this.reopenAfterSignup(initialPanel.dataset.voteInitialPanel);
  }

  disconnectedCallback() {
    clearInterval(this.countdownTimer);
    clearTimeout(this.pollTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const voteButton = event.target.closest('[data-vote-button]');
      if (voteButton) this.askForEmail(voteButton.closest('[data-design-id]'));
      if (event.target.closest('[data-vote-lookup-open]')) this.openLookup();
      if (event.target.closest('[data-vote-signup-open]')) this.openSignup();
      if (event.target.closest('[data-vote-show-results]')) this.showResults();
      if (event.target.closest('[data-vote-results-retry]')) this.refreshResults();
      if (event.target.closest('[data-vote-dismiss]')) this.closeDialog();
      if (event.target.closest('[data-vote-copy]')) this.copyCode(event.target.closest('[data-vote-copy]'));
    });
    // A tap on the dimmed backdrop lands on the <dialog> itself.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeDialog();
    });
    this.dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
    this.form.addEventListener('submit', (event) => this.lockInVote(event));
    this.lookupForm = this.dialog.querySelector('[data-vote-lookup-form]');
    this.lookupForm.addEventListener('submit', (event) => this.findVoteByEmail(event));
    // Polling sleeps in a background tab; coming back shows fresh numbers at once.
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && (this.vote || this.returningVoter || this.isClosed())) this.refreshResults();
      else this.schedulePoll();
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  isClosed() {
    return Date.now() >= this.closesAt;
  }

  tickCountdown() {
    const remaining = Math.max(0, this.closesAt - Date.now());
    const totalSeconds = Math.floor(remaining / ChristmasVote.SECOND_MS);
    const parts = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor(totalSeconds / 3600) % 24,
      minutes: Math.floor(totalSeconds / 60) % 60,
      seconds: totalSeconds % 60,
    };
    Object.entries(parts).forEach(([unit, value]) => {
      const slot = this.querySelector(`[data-countdown="${unit}"]`);
      if (slot) slot.textContent = String(value).padStart(2, '0');
    });
    if (remaining === 0 && !this.hasAttribute('data-closed')) this.closeVoting();
  }

  closeVoting() {
    this.setAttribute('data-closed', '');
    clearInterval(this.countdownTimer);
    this.refreshResults();
  }

  askForEmail(card) {
    if (!card || this.vote || this.isClosed()) return;
    this.showPendingDesign(card);
    this.setError('');
    this.openDialog('vote');
  }

  showPendingDesign(card) {
    this.pendingCard = card;
    this.dialog.querySelector('[data-vote-pending-title]').textContent = card.dataset.designTitle;
    this.dialog.querySelector('[data-vote-pending-image]').src = card.dataset.designThumbnail;
  }

  // Back from Shopify's form: the code after a signup, or the email field again
  // when the address was rejected (the vote already counts by then).
  reopenAfterSignup(panel) {
    const votedCard = this.vote && this.cards.find((card) => card.dataset.designId === this.vote.designId);
    if (panel === 'vote' && votedCard) this.showPendingDesign(votedCard);
    this.openDialog(panel);
  }

  openSignup() {
    if (!this.vote) return;
    const emailPanel = this.dialog.querySelector('[data-vote-panel="vote"]');
    if (emailPanel) {
      const votedCard = this.cards.find((card) => card.dataset.designId === this.vote.designId);
      if (votedCard) this.showPendingDesign(votedCard);
      this.setBusy(this.form.querySelector('[data-vote-submit]'), false);
      this.setError('');
    }
    this.openDialog(emailPanel ? 'vote' : 'code');
  }

  async lockInVote(event) {
    // Back from a rejected email the vote already counts: just retry the signup.
    if (this.vote) {
      this.tagVoter();
      this.setReturnTo();
      this.closeDialog();
      return;
    }
    event.preventDefault();
    if (!this.pendingCard || this.busy) return;

    // Keep duplicate taps out while the vote service records the choice.
    const submitButton = this.form.querySelector('[data-vote-submit]');
    this.setBusy(submitButton, true);
    this.setError('');

    try {
      const emailKey = await window.ChristmasVoteStore.emailKey(this.form.elements['contact[email]'].value);
      const response = await this.store.castVote(this.pendingCard.dataset.designId, emailKey);
      if (response.error === 'VOTING_CLOSED') {
        this.closeDialog();
        this.closeVoting();
        return;
      }
      if (!response.ok && response.error !== 'ALREADY_VOTED') throw new Error(response.error);

      // ALREADY_VOTED with this email: the earlier vote stands, the signup still goes on.
      this.rememberVote(response.votedFor || this.pendingCard.dataset.designId, response.voterId);
      this.renderResults(response);
      this.tagVoter();
      this.setReturnTo();
      // Shopify wraps form.submit() to obtain the CAPTCHA token. Calling the
      // native prototype directly skips that wrapper and rejects the signup.
      this.setBusy(submitButton, false);
      // hCaptcha mounts outside this <dialog>. Leave the browser's modal top
      // layer before verification so the challenge is visible and interactive.
      this.closeDialog();
      const submit = () => this.form.submit();
      if (window.Shopify && window.Shopify.captcha) window.Shopify.captcha.protect(this.form, submit);
      else submit();
    } catch (error) {
      this.setError(this.strings.error);
      this.setBusy(submitButton, false);
    }
  }

  openLookup() {
    if (this.vote || this.isClosed()) return;
    this.setError('', '[data-vote-lookup-error]');
    this.openDialog('lookup');
  }

  // No memory of the vote in this browser: ask the sheet by email key.
  async findVoteByEmail(event) {
    event.preventDefault();
    if (this.busy) return;
    const submitButton = this.lookupForm.querySelector('[type="submit"]');
    this.setBusy(submitButton, true);
    this.setError('', '[data-vote-lookup-error]');

    try {
      const emailKey = await window.ChristmasVoteStore.emailKey(this.lookupForm.elements.email.value);
      const response = await this.store.fetchResults(emailKey);
      if (!response.votedFor) {
        this.setError(this.strings.lookup_not_found, '[data-vote-lookup-error]');
        return;
      }
      this.rememberVote(response.votedFor, this.store.voterId());
      this.renderResults(response);
      this.schedulePoll();
      this.closeDialog();
    } catch (error) {
      this.setError(this.strings.error, '[data-vote-lookup-error]');
    } finally {
      this.setBusy(submitButton, false);
    }
  }

  rememberVote(designId, voterId) {
    this.vote = { designId, voterId };
    this.store.saveVote(designId, voterId);
    this.markVoted(designId);
  }

  setBusy(button, busy) {
    this.busy = busy;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  tagVoter() {
    const tags = this.form.querySelector('[data-vote-tags]');
    if (tags) tags.value = `${tags.dataset.baseTags},voted-${this.vote.designId}`;
  }

  setReturnTo() {
    const returnTo = this.form.elements.return_to;
    if (!returnTo) return;
    const url = new URL(window.location.href);
    url.searchParams.set('customer_posted', 'true');
    url.searchParams.set('vote_voter', this.vote.voterId);
    returnTo.value = `${url.pathname}${url.search}#ChristmasVoteForm`;
  }

  showResults() {
    this.closeDialog();
    this.refreshResults();
    this.querySelector('[data-vote-grid]').scrollIntoView({ block: 'start' });
  }

  markVoted(designId) {
    this.setAttribute('data-voted', '');
    this.cards.forEach((card) => card.toggleAttribute('data-your-vote', card.dataset.designId === designId));
  }

  async refreshResults() {
    if (!this.hasAttribute('data-results') && (this.vote || this.returningVoter || this.isClosed())) {
      this.setResultsStatus(this.strings.results_loading);
    }
    try {
      const response = await this.store.fetchResults(undefined, this.returningVoter);
      if (response.votedFor && !this.vote) {
        this.rememberVote(response.votedFor, this.returningVoter || this.store.voterId());
      }
      if (this.returningVoter && this.vote) {
        const url = new URL(window.location.href);
        url.searchParams.delete('vote_voter');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        this.returningVoter = null;
      }
      if (this.vote || this.isClosed()) this.renderResults(response);
    } catch (error) {
      if (!this.hasAttribute('data-results')) this.setResultsStatus(this.strings.results_error, true);
    }
    this.schedulePoll();
  }

  schedulePoll() {
    clearTimeout(this.pollTimer);
    const showsResults = this.vote || this.returningVoter || this.isClosed();
    if (!showsResults || document.visibilityState !== 'visible') return;
    this.pollTimer = setTimeout(() => this.refreshResults(), ChristmasVote.POLL_INTERVAL_MS);
  }

  renderResults({ counts, total }) {
    if (!counts) return;
    this.store.saveResults({ counts, total });
    const ranking = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    const totalVotes = total || 0;

    this.cards.forEach((card) => {
      const count = counts[card.dataset.designId] || 0;
      const share = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
      const rank = ranking.indexOf(card.dataset.designId) + 1;
      card.style.setProperty('--vote-share', `${share}%`);
      card.querySelector('[data-vote-share]').textContent = `${share}%`;
      card.querySelector('[data-vote-count]').textContent = this.formatVotes(count);
      card.querySelector('[data-vote-rank]').textContent = `#${rank}`;
      card.toggleAttribute('data-top-place', count > 0 && rank <= ChristmasVote.TOP_PLACES);
    });

    this.querySelectorAll('[data-vote-total]').forEach((node) => {
      node.textContent = this.formatVotes(totalVotes);
    });
    this.setAttribute('data-results', '');
    this.setResultsStatus('');
  }

  setResultsStatus(message, retry = false) {
    const status = this.querySelector('[data-vote-results-status]');
    if (!status) return;
    status.hidden = !message;
    status.querySelector('[data-vote-results-message]').textContent = message;
    status.querySelector('[data-vote-results-retry]').hidden = !retry;
  }

  formatVotes(count) {
    const template = count === 1 ? this.strings.votes_one : this.strings.votes_other;
    return template.replace('[count]', this.numberFormat.format(count));
  }

  openDialog(panel) {
    // After a signup on this page Shopify renders the code panel instead of the email one.
    const panels = Array.from(this.dialog.querySelectorAll('[data-vote-panel]'));
    const target =
      panels.find((node) => node.dataset.votePanel === panel) ||
      panels.find((node) => node.dataset.votePanel === 'code');
    panels.forEach((node) => {
      node.hidden = node !== target;
    });
    this.dialog.setAttribute('aria-labelledby', target.querySelector('h2').id);
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('overflow-hidden');
  }

  closeDialog() {
    if (this.dialog.open) this.dialog.close();
  }

  setError(message, selector = '[data-vote-error]') {
    const error = this.dialog.querySelector(selector);
    error.textContent = message;
    error.hidden = !message;
  }

  async copyCode(button) {
    try {
      await navigator.clipboard.writeText(button.dataset.code);
      button.textContent = this.strings.copied;
    } catch (error) {
      // No clipboard access (old browser, iframe): the code stays visible to copy by hand.
    }
  }
}

customElements.define('christmas-vote', ChristmasVote);
